import { hasRole } from './permissions';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { api, getToken, setToken, setUnauthorizedHandler } from './api';
import type { LoginResult, User } from './types';

type AuthStatus = 'loading' | 'authed' | 'anon';

interface AuthContextValue {
  user: User | null;
  status: AuthStatus;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const mounted = useRef(true);

  const handleUnauthorized = useCallback(() => {
    if (!mounted.current) return;
    setToken('');
    setUser(null);
    setStatus('anon');
  }, []);

  useEffect(() => {
    mounted.current = true;
    setUnauthorizedHandler(handleUnauthorized);
    return () => { mounted.current = false; setUnauthorizedHandler(null); };
  }, [handleUnauthorized]);

  // 启动时尝试用本地 token 恢复会话（/api/auth/me 返回 {user}，非 {data} 信封）。
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!getToken()) { setStatus('anon'); return; }
      try {
        const res = await api.get<{ user: User }>('/api/auth/me');
        if (cancelled) return;
        setUser(res.user);
        setStatus('authed');
      } catch {
        if (cancelled) return;
        setToken('');
        setStatus('anon');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const res = await api.post<LoginResult>('/api/auth/login', { username, password });
    setToken(res.token);
    if (!mounted.current) return;
    setUser(res.user);
    setStatus('authed');
  }, []);

  const logout = useCallback(() => {
    // best-effort 通知后端销毁会话；无论成功都清理本地。
    api.post('/api/auth/logout').catch(() => { /* ignore */ });
    setToken('');
    setUser(null);
    setStatus('anon');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, login, logout, isAdmin: !!user && hasRole(user, 'admin') }),
    [user, status, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth 必须在 AuthProvider 内使用');
  return ctx;
}
